import { useState, useEffect, useCallback } from 'react';
import { contactsAPI } from '../../api/contacts';
import { Search, Mail, Eye, Check, Archive, Reply, RefreshCw, Filter } from 'lucide-react';
import Loader from '../common/Loader';
import Notification from '../common/Notification';
import '../../styles/admin/AdminContacts.css';

export default function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [notification, setNotification] = useState(null);
  const [stats, setStats] = useState({
    new: 0,
    read: 0,
    replied: 0,
    archived: 0,
    total: 0
  });

  const loadContacts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      
      const response = await contactsAPI.getAll(params);
      const contactsData = response.data?.contacts || response.data || [];
      setContacts(Array.isArray(contactsData) ? contactsData : []);
      
      // Calculer les stats
      const stats = {
        new: contactsData.filter(c => c.status === 'new').length,
        read: contactsData.filter(c => c.status === 'read').length,
        replied: contactsData.filter(c => c.status === 'replied').length,
        archived: contactsData.filter(c => c.status === 'archived').length,
        total: contactsData.length
      };
      setStats(stats);
      
    } catch (err) {
      console.error('Erreur chargement contacts:', err);
      setError(err.message || 'Erreur lors du chargement des contacts');
      showNotification('error', 'Erreur lors du chargement des contacts');
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await contactsAPI.updateStatus(id, newStatus);
      
      // Mettre à jour localement
      setContacts(prev => prev.map(contact => 
        contact._id === id ? { ...contact, status: newStatus } : contact
      ));
      
      // Mettre à jour les stats
      setStats(prev => {
        const oldStatus = contacts.find(c => c._id === id)?.status;
        const newStats = { ...prev };
        
        if (oldStatus && oldStatus !== newStatus) {
          newStats[oldStatus] = Math.max(0, newStats[oldStatus] - 1);
        }
        newStats[newStatus] = (newStats[newStatus] || 0) + 1;
        
        return newStats;
      });
      
      // Mettre à jour le contact sélectionné si ouvert
      if (selectedContact && selectedContact._id === id) {
        setSelectedContact(prev => ({ ...prev, status: newStatus }));
      }
      
      showNotification('success', `Statut mis à jour: ${formatStatus(newStatus)}`);
      
    } catch (err) {
      console.error('Erreur mise à jour statut:', err);
      showNotification('error', 'Erreur lors de la mise à jour du statut');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Date invalide';
      
      const now = new Date();
      const diffMs = now - date;
      const diffMinutes = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMinutes < 1) return 'À l\'instant';
      if (diffMinutes < 60) return `Il y a ${diffMinutes} min`;
      if (diffHours < 24) return `Il y a ${diffHours} h`;
      if (diffDays < 7) return `Il y a ${diffDays} j`;
      
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Date invalide';
    }
  };

  const formatStatus = (status) => {
    const statusMap = {
      new: 'Nouveau',
      read: 'Lu',
      replied: 'Répondu',
      archived: 'Archivé'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'status-new',
      read: 'status-read',
      replied: 'status-replied',
      archived: 'status-archived'
    };
    return colors[status] || 'status-default';
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
  };

  const handleRefresh = () => {
    loadContacts();
  };

  if (loading && contacts.length === 0) {
    return <Loader />;
  }

  return (
    <div className="admin-contacts-section">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Header */}
      <div className="section-header">
        <div>
          <h2>Messages des Contacts</h2>
          <p className="subtitle">{stats.total} messages reçus</p>
        </div>
        
        <div className="header-actions">
          <button 
            className="btn-secondary"
            onClick={handleRefresh}
            disabled={loading}
          >
            <RefreshCw size={20} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="contacts-filters">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Rechercher dans les messages..."
            value={search}
            onChange={handleSearch}
          />
        </div>
        
        <div className="filter-group">
          <Filter size={18} />
          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">Tous les statuts</option>
            <option value="new">Nouveaux</option>
            <option value="read">Lus</option>
            <option value="replied">Répondu</option>
            <option value="archived">Archivés</option>
          </select>
        </div>
      </div>

      {/* Statistiques */}
      <div className="stats-grid">
        <div className="stat-card stat-new">
          <h3>{stats.new}</h3>
          <p>Nouveaux</p>
        </div>
        <div className="stat-card stat-read">
          <h3>{stats.read}</h3>
          <p>Lus</p>
        </div>
        <div className="stat-card stat-replied">
          <h3>{stats.replied}</h3>
          <p>Répondu</p>
        </div>
        <div className="stat-card stat-total">
          <h3>{stats.total}</h3>
          <p>Total</p>
        </div>
      </div>

      {/* Tableau des contacts */}
      <div className="contacts-table-container">
        <table className="contacts-table">
          <thead>
            <tr>
              <th>Expéditeur</th>
              <th>Sujet</th>
              <th>Message</th>
              <th>Date</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {contacts.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-data">
                  {loading ? (
                    <div className="loading-row">
                      <div className="spinner"></div>
                      Chargement...
                    </div>
                  ) : error ? (
                    <div className="error-row">
                      ❌ {error}
                    </div>
                  ) : (
                    <div className="empty-row">
                      📭 Aucun message trouvé
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              contacts.map((contact) => (
                <tr key={contact._id} className={contact.status === 'new' ? 'new-contact' : ''}>
                  <td>
                    <div className="contact-sender">
                      <div className="sender-name">
                        <strong>{contact.name}</strong>
                        {contact.company && (
                          <span className="company">{contact.company}</span>
                        )}
                      </div>
                      <div className="sender-contact">
                        <a href={`mailto:${contact.email}`}>{contact.email}</a>
                        {contact.phone && (
                          <div className="phone">{contact.phone}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="contact-subject">
                      <strong>{contact.subject}</strong>
                    </div>
                  </td>
                  <td>
                    <div className="message-preview">
                      {contact.message.substring(0, 80)}...
                      <div className="message-length">
                        {contact.message.length} caractères
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="contact-date">
                      {formatDate(contact.createdAt)}
                    </div>
                  </td>
                  <td>
                    <div className={`status-badge ${getStatusColor(contact.status)}`}>
                      {formatStatus(contact.status)}
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        onClick={() => setSelectedContact(contact)}
                        className="icon-btn view-btn"
                        title="Voir le message"
                      >
                        <Eye size={18} />
                      </button>
                      
                      {contact.status !== 'read' && (
                        <button 
                          onClick={() => updateStatus(contact._id, 'read')}
                          className="icon-btn read-btn"
                          title="Marquer comme lu"
                        >
                          <Check size={18} />
                        </button>
                      )}
                      
                      {contact.status !== 'replied' && (
                        <button 
                          onClick={() => updateStatus(contact._id, 'replied')}
                          className="icon-btn replied-btn"
                          title="Marquer comme répondu"
                        >
                          <Reply size={18} />
                        </button>
                      )}
                      
                      {contact.status !== 'archived' && (
                        <button 
                          onClick={() => updateStatus(contact._id, 'archived')}
                          className="icon-btn archive-btn"
                          title="Archiver"
                        >
                          <Archive size={18} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal détails contact */}
      {selectedContact && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>
                <Mail size={20} />
                Message de {selectedContact.name}
              </h3>
              <button 
                onClick={() => setSelectedContact(null)} 
                className="close-btn"
              >
                ×
              </button>
            </div>
            
            <div className="contact-details">
              <div className="detail-section">
                <h4>
                  <span className="detail-icon">👤</span>
                  Informations de contact
                </h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <strong>Nom:</strong>
                    <span>{selectedContact.name}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Email:</strong>
                    <a href={`mailto:${selectedContact.email}`}>
                      {selectedContact.email}
                    </a>
                  </div>
                  {selectedContact.phone && (
                    <div className="detail-item">
                      <strong>Téléphone:</strong>
                      <a href={`tel:${selectedContact.phone}`}>
                        {selectedContact.phone}
                      </a>
                    </div>
                  )}
                  {selectedContact.company && (
                    <div className="detail-item">
                      <strong>Entreprise:</strong>
                      <span>{selectedContact.company}</span>
                    </div>
                  )}
                  <div className="detail-item">
                    <strong>Date:</strong>
                    <span>{formatDate(selectedContact.createdAt)}</span>
                  </div>
                  {selectedContact.ipAddress && (
                    <div className="detail-item">
                      <strong>Adresse IP:</strong>
                      <code>{selectedContact.ipAddress}</code>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="detail-section">
                <h4>
                  <span className="detail-icon">📝</span>
                  Sujet du message
                </h4>
                <p className="subject-text">{selectedContact.subject}</p>
              </div>
              
              <div className="detail-section">
                <h4>
                  <span className="detail-icon">💬</span>
                  Message
                </h4>
                <div className="message-content">
                  {selectedContact.message.split('\n').map((line, idx) => (
                    <p key={idx}>{line}</p>
                  ))}
                </div>
              </div>
              
              <div className="detail-actions">
                <a 
                  href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}&body=Bonjour ${selectedContact.name},%0D%0A%0D%0A`}
                  className="btn-primary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Mail size={20} />
                  Répondre par email
                </a>
                
                <div className="status-actions">
                  <button 
                    onClick={() => {
                      updateStatus(selectedContact._id, 'read');
                    }}
                    className="btn-secondary"
                    disabled={selectedContact.status === 'read'}
                  >
                    <Check size={18} />
                    Marquer comme lu
                  </button>
                  <button 
                    onClick={() => {
                      updateStatus(selectedContact._id, 'replied');
                    }}
                    className="btn-secondary"
                    disabled={selectedContact.status === 'replied'}
                  >
                    <Reply size={18} />
                    Marquer comme répondu
                  </button>
                  <button 
                    onClick={() => {
                      updateStatus(selectedContact._id, 'archived');
                      setSelectedContact(null);
                    }}
                    className="btn-secondary danger"
                  >
                    <Archive size={18} />
                    Archiver
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}